import { AfterViewInit, ApplicationRef, Component, createComponent, ElementRef, inject, Injector, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { PublicQueryService } from '../../services/public-query.service';
import { FaviconService } from '../../services/favicon.service';
import { AcelenData } from '../../interfaces/public-query.interface';

import { MoreInfoModalComponent } from './more-info-modal/more-info-modal.component';
import { ModalConfirmComponent } from '../../components/modal-confirm/modal-confirm.component';
import { MapPopupComponent } from './map-popup/map-popup.component';
import { ModalProtocolDetailsComponent } from './modal-protocol-details/modal-protocol-details.component';

import * as AOS from 'aos';
import * as L from 'leaflet';
import { GoogleChartsModule } from 'angular-google-charts';
import { ChartType } from 'angular-google-charts';

@Component({
  selector: 'app-acelen-public-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GoogleChartsModule
  ],
  templateUrl: './acelen-public-page.component.html',
  styleUrl: './acelen-public-page.component.scss'
})

export class AcelenPublicPageComponent implements OnInit, AfterViewInit, OnDestroy {
  injector = inject(Injector);
  appRef = inject(ApplicationRef);
  publicQueryService = inject(PublicQueryService);
  activatedRoute = inject(ActivatedRoute);
  formBuilder = inject(FormBuilder);
  dialog = inject(MatDialog);
  titleService = inject(Title);
  faviconService = inject(FaviconService);

  protocolMockList: any[] = [{
    name: 'Protocolo A',
    value: 'Protocolo A'
  },
  {
    name: 'Protocolo B',
    value: 'Protocolo B'
  },
  {
    name: 'Protocolo C',
    value: 'Protocolo C'
  }
  ];
  protocolResult = '';
  protocolColor = '';
  reasons: string[] = [];
  rotateButton = false;
  hiddenHeader = false;
  map: any;
  private resizeObserver: any;
  private mutationObserver: any;
  identifier = '';
  form: FormGroup = this.formBuilder.group({
    code: ['', Validators.required],
    protocol: ['']
  });
  productData?: AcelenData;
  placesCentroids: L.Marker[] = [];
  haveFarming = false;
  haveMilling = false;
  haveRefinery = false;
  haveOutbounds = false;
  chartData: any[] = [];
  chartOptions = {
    title: '',
    pieHole: 0.6,
    legend: {
      position: 'bottom'
    }
  }
  chartType: ChartType = ChartType.PieChart;
  totalFootprint = 0;

  @ViewChild('mapArea', { static: false }) mapArea!: ElementRef;
  @ViewChild('sentinela', { static: false }) sentinela!: ElementRef;
  @ViewChild('popupTemplate', { static: true }) popupTemplate!: TemplateRef<any>;

  ngOnInit(): void {
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });

    this.titleService.setTitle('Acelen - Painel Público');
    this.faviconService.changeFavicon('assets/images/acelen/favicon.png');

    this.activatedRoute.queryParamMap.subscribe(params => {
      this.identifier = params.get('id')!;
      if (this.identifier) {
        this.form.get('code')?.setValue(this.identifier.trim());
        this.getProductInfo();
      }
    });
    setTimeout(() => {
      try { if (this.map && typeof this.map.invalidateSize === 'function') this.map.invalidateSize(); } catch (e) {}
    }, 300);
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.initHeaderObserver();

    try {
      if (this.mapArea && (window as any).ResizeObserver) {
        this.resizeObserver = new (window as any).ResizeObserver(() => {
          this.refreshMap(20);
        });
        this.resizeObserver.observe(this.mapArea.nativeElement);
      } else if (this.mapArea) {
        this.mutationObserver = new MutationObserver(() => {
          this.refreshMap(80);
        });
        this.mutationObserver.observe(this.mapArea.nativeElement, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    } catch (e) {}
  }

  addMarkersAndFlow(): void {
    if (!this.map) {
      console.error('Mapa não inicializado!');
      return;
    }
    const flowCoords: L.LatLngExpression[] = [];
    this.placesCentroids.forEach(marker => marker.remove());
    this.placesCentroids = [];
    this.productData!.stages.forEach((stage: any) => {
      const marker = L.marker(
        [parseFloat(stage.place.latitude), parseFloat(stage.place.longitude)],
        { icon: this.checkResult(stage.name) }
      ).addTo(this.map).bindPopup(this.createPopupContent(this.formatPlace(stage)));
      this.placesCentroids.push(marker);
      flowCoords.push([parseFloat(stage.place.latitude), parseFloat(stage.place.longitude)]);
    });

    // Desenha a polyline ligando na ordem
    const polyline = L.polyline(flowCoords, {
      color: '#ffffff',
      weight: 3,
      opacity: 1,
      dashArray: '5,10'  // opcional: linha tracejada
    }).addTo(this.map);

    // Adiciona setas com o decorator
    try {
      const decorator = (L as any).polylineDecorator(polyline, {
        patterns: [
          {
            offset: '1%',    // começa 1% ao longo da linha
            repeat: '1%',    // repete a cada 1%
            symbol: (L as any).Symbol.arrowHead({
              pixelSize: 8,
              polygon: false,
              pathOptions: { stroke: true, color: '#368EFA', weight: 2 }
            })
          }
        ]
      }).addTo(this.map);
    } catch {
      console.log('Erro ao renderizar setas nas linhas.')
    }

    if (flowCoords.length > 0) {
      const [lat, lng] = flowCoords[0] as [number, number];
      this.map.setView([lat, lng], 2);
    }
  }

  getProductInfo(): void {
    if (this.form.valid) {
      this.publicQueryService.findAcelenProduct(this.form.get('code')?.value).subscribe({
        next: (res: AcelenData) => {
          if (res) {
            this.productData = res;
            this.calculateInputFootPrint();
            this.addMarkersAndFlow();
            this.checkStepPresence(this.productData);
            this.reasons = [];
            this.protocolResult = '';
            this.protocolColor = '';
            if (this.form.get('protocol')?.value) {
              this.checkProtocolResult(this.form.get('protocol')?.value, this.productData);
            }
            this.refreshMap(120);
          }
        },
        error: (error: Error) => {
          this.dialog.open(ModalConfirmComponent, {
            data: {
              error: true,
              text: 'Erro ao buscar dados do item no momento',
              subText: 'Tente novamente mais tarde.',
              hideCancelButton: true
            }
          })
        }
      });
    } else {
      this.form.markAllAsTouched();
      this.dialog.open(ModalConfirmComponent, {
        data: {
          error: true,
          text: 'Preencha um Código de Rastreamento',
          hideCancelButton: true
        }
      })
    }

  }

  checkProtocolResult(protocolName: string, productData: any): void {
    switch (protocolName) {
      case ('Protocolo A'):
        const protocolAfarmingStage: any = productData.stages.find((stage: any) => stage.name === 'FARMING');
        const protocolArefineryStage: any = productData.stages.find((stage: any) => stage.name === 'REFINERY');
        if (protocolAfarmingStage && protocolArefineryStage) {
          if (Number(protocolAfarmingStage.footPrint) <= 50 || Number(protocolArefineryStage.footPrint) <= 110) {
            this.protocolColor = 'green';
            this.protocolResult = 'APROVADO';
            this.reasons.push('Emissão no processo de Agricultura é menor ou igual que 50kg CO2 eq/t.', 'Emissão no processo de Refinaria é menor ou igual que 110kg CO2 eq/t.');
          } else {
            this.protocolColor = 'red';
            this.protocolResult = 'REPROVADO';
            if (Number(protocolAfarmingStage.footPrint) >= 50) {
              this.reasons.push('Emissão no processo de Agricultura é maior que 50kg CO2 eq/t.')
            }
            if (Number(protocolArefineryStage.footPrint) >= 110) {
              this.reasons.push('Emissão no processo de Refinaria é maior que 110kg CO2 eq/t.')
            }
          }
        } else {
          this.protocolColor = '#ccc';
          this.protocolResult = 'NÃO SE APLICA';
          if (!protocolAfarmingStage) {
            this.reasons.push('Etapa de Agricultura não está presente na cadeia.')
          } else {
            this.reasons.push('Etapa de Refinaria não está presente na cadeia.')
          }
        }
        break;
      case ('Protocolo B'):
        const protocolBoutboundStage: any = productData.stages.find((stage: any) => stage.name === 'OUTBOUND_LOGISTICS');
        if (protocolBoutboundStage) {
          if (Number(protocolBoutboundStage.footPrint) <= 0.5) {
            this.protocolColor = 'green';
            this.protocolResult = 'APROVADO';
            this.reasons.push('Emissão no processo de Logísticas de Saída é menor que 0.5kg CO2 eq/t.');
          } else {
            this.protocolColor = 'red';
            this.protocolResult = 'REPROVADO';
            this.reasons.push('Emissão no processo de Logísticas de Saída é maior que 0.5kg CO2 eq/t.');
          }
        } else {
          this.protocolColor = '#ccc';
          this.protocolResult = 'NÃO SE APLICA';
          this.reasons.push('Etapa de Logísticas de Saída não está presente na cadeia.')
        }
        break;
      case ('Protocolo C'):
        const protocolCfarmingStage: any = productData.stages.find((stage: any) => stage.name === 'FARMING');
        let protocolCfarmingDiesel: any = protocolCfarmingStage ? protocolCfarmingStage.inputs.find((input: any) => input.name === 'diesel s10') : undefined;
        protocolCfarmingDiesel = protocolCfarmingDiesel ? Number(protocolCfarmingDiesel.amount) : undefined;
        if (protocolCfarmingStage && protocolCfarmingDiesel) {
          if (Number(protocolCfarmingStage.footPrint) <= 50 || protocolCfarmingDiesel <= 10000) {
            this.protocolColor = 'green';
            this.protocolResult = 'APROVADO';
            this.reasons.push('Emissão no processo de Agricultura é menor ou igual que 50kg CO2 eq/t.', 'Uso do insumo de Diesel é inferior a 10000L.');
          } else {
            this.protocolColor = 'red';
            this.protocolResult = 'REPROVADO';
            if (Number(protocolCfarmingStage.footPrint) >= 50) {
              this.reasons.push('Emissão no processo de Agricultura é maior que 50kg CO2 eq/t.')
            }
            if (protocolCfarmingDiesel >= 10000) {
              this.reasons.push('Uso do insumo de Diesel é superior a 10000L.')
            }
          }
        } else {
          this.protocolColor = '#ccc';
          this.protocolResult = 'NÃO SE APLICA';
          if (!protocolCfarmingStage) {
            this.reasons.push('Etapa de Agricultura não está presente na cadeia.')
          } else {
            this.reasons.push('Insumo de Diesel não está presente na etapa.')
          }
        }
        break;
    }
  }

  formatPlace(stage: any): any[] {
    return {
      ...stage.result,
      inputs: stage.inputs,
      processName: this.translateProcessName(stage.name),
      localName: stage.place.nickname
    };
  }

  checkResult(place: string): any {
    switch (place) {
      case ('FARMING'):
        return this.createPin('blue');
      case ('MILLING'):
        return this.createPin('green');
      case ('REFINERY'):
        return this.createPin('yellow');
      case ('OUTBOUND_LOGISTICS'):
        return this.createPin('red');
    }
  }

  translateProcessName(process: string): string {
    switch (process) {
      case ('FARMING'):
        return 'Agricultura';
      case ('MILLING'):
        return 'Moagem';
      case ('REFINERY'):
        return 'Refinaria';
      case ('OUTBOUND_LOGISTICS'):
        return 'Logísticas de Saída';
      default:
        return '-'
    }
  }

  initMap(): void {
    if (this.map) {
      try { this.refreshMap(60); } catch (e) {}
      return;
    }

    if (!this.mapArea || !this.mapArea.nativeElement) return;
    const el = this.mapArea.nativeElement as HTMLElement;
    if (el.clientWidth === 0 || el.clientHeight === 0) {
      setTimeout(() => this.initMap(), 200);
      return;
    }

    this.map = L.map(this.mapArea.nativeElement, {
      attributionControl: false,
      minZoom: 2,
      maxBounds: [
        [-180, -180],
        [180, 180]
      ],
      maxBoundsViscosity: 1.0,
      scrollWheelZoom: false
    }).setView([-6.9714, -58.5014], 4);

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Acelen',
    }).addTo(this.map);

    L.control.attribution({
      position: 'topright'
    }).addTo(this.map);

    L.control.scale({ position: 'topleft' }).addTo(this.map);

    const editableLayers = new L.FeatureGroup();
    this.map.addLayer(editableLayers);
  }

  refreshMap(delay: number = 50): void {
    try {
      setTimeout(() => {
        try {
          if (this.map && typeof this.map.invalidateSize === 'function') {
            requestAnimationFrame(() => {
              try { this.map.invalidateSize(false); } catch (e) {}
              requestAnimationFrame(() => {
                try { this.map.invalidateSize(false); } catch (e) {}
                try {
                  this.map.eachLayer((layer: any) => {
                    if (layer && typeof layer.redraw === 'function') {
                      layer.redraw();
                    }
                  });
                } catch (e) {}
              });
            });
          }
        } catch (e) {}
      }, delay);
    } catch (e) {}
  }

  initHeaderObserver(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.hiddenHeader = !entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(this.sentinela.nativeElement);
  }

  checkStepPresence(productData: AcelenData): void {
    productData.stages.forEach((stage: any) => {
      if (productData.stages.length > 0) {
        if (stage.name === 'FARMING') {
          this.haveFarming = true;
        }
        if (stage.name === 'MILLING') {
          this.haveMilling = true;
        }
        if (stage.name === 'REFINERY') {
          this.haveRefinery = true;
        }
        if (stage.name === 'OUTBOUND_LOGISTICS') {
          this.haveOutbounds = true;
        }
      }
    });
  }

  createPopupContent(stage: any): HTMLElement {
    const componentRef = createComponent(MapPopupComponent, {
      environmentInjector: this.appRef.injector
    });

    componentRef.instance.stage = stage;

    this.appRef.attachView(componentRef.hostView);
    const div = document.createElement('div');
    div.appendChild((componentRef.hostView as any).rootNodes[0]);
    return div;
  }

  createPin(color: string): L.Icon {
    return L.icon({
      iconUrl: `../../../assets/images/acelen/pin-${color}.svg`,
      iconSize: [20, 36],
      popupAnchor: [0, -50]
    });
  }

  openMoreInfoModal(process: string): void {
    this.dialog.open(MoreInfoModalComponent, {
      data: {
        process: this.translateProcessName(process),
        stage: this.productData?.stages.find((stage: any) => stage.name === process)
      }
    })
  }

  ngOnDestroy(): void {
    try {
      if (this.resizeObserver && typeof this.resizeObserver.disconnect === 'function') this.resizeObserver.disconnect();
    } catch (e) {}
    try {
      if (this.mutationObserver && typeof this.mutationObserver.disconnect === 'function') this.mutationObserver.disconnect();
    } catch (e) {}
  }

  openProtocolModal(protocolName: string, reasons?: string[]): void {
    this.dialog.open(ModalProtocolDetailsComponent, {
      data: {
        protocolName: protocolName,
        reasons: reasons
      }
    });
  }

  openPopup(stage: any, lat: number, lng: number): void {
    const popup = L.popup()
      .setLatLng([lat, lng])
      .setContent(this.createPopupContent(stage))
      .openOn(this.map!);
  }

  calculateInputFootPrint(): void {
    let footPrintsSum = 0;
    this.productData?.stages.forEach((item: any) => {
      if (footPrintsSum != 0) {
        footPrintsSum = footPrintsSum + Number(item.footPrint);
      } else {
        footPrintsSum = Number(item.footPrint);
      }
      this.chartData.push([(item.name).toUpperCase(), Number(item.footPrint)])
    });
    this.totalFootprint = Number(footPrintsSum.toFixed(2));
  }
}
